import { matchPassioRoute, PASSIO_ROUTES, type AvailableModel, type PassioCallRecord, type PassioRouteDef } from "@passio/api-client";
import { createStore, useStore } from "./store";

const KEY = "passio-demo.route-models.v1";

/** Routes that accept `?model=`. */
export const AI_ROUTES: PassioRouteDef[] = PASSIO_ROUTES.filter((r) => r.ai);
export const aiRoute = (id: string) => AI_ROUTES.find((r) => r.id === id);
/** Image-only models are offered on photo routes only. */
export const isVisionRoute = (id: string) => aiRoute(id)?.feature === "vision";

function load(): Record<string, string> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, unknown>;
    return Object.fromEntries(Object.entries(raw).filter((e): e is [string, string] => typeof e[1] === "string" && !!e[1] && !!aiRoute(e[0])));
  } catch {
    return {};
  }
}

/** Route id to model tag; routes not listed run their own default model. */
export const routeModels = createStore<Record<string, string>>(load());

export function setRouteModels(next: Record<string, string>) {
  const clean = Object.fromEntries(Object.entries(next).filter(([, v]) => v));
  routeModels.set(clean);
  if (Object.keys(clean).length) localStorage.setItem(KEY, JSON.stringify(clean));
  else localStorage.removeItem(KEY);
}

export const setRouteModel = (routeId: string, tag: string) => setRouteModels({ ...routeModels.get(), [routeId]: tag });
export const getRouteModel = (routeId: string): string | undefined => routeModels.get()[routeId] || undefined;
export const useRouteModels = () => useStore(routeModels);

/** The model that answered the most recent call to each route (from `X-Model-Tag`). */
export const servedModels = createStore<Record<string, string>>({});
export const useServedModels = () => useStore(servedModels);

export function trackServedModel(r: PassioCallRecord) {
  const tag = r.meta?.modelTag;
  const route = tag ? matchPassioRoute(r.method as "GET" | "POST", r.path) : undefined;
  if (tag && route?.ai && servedModels.get()[route.id] !== tag) servedModels.set((m) => ({ ...m, [route.id]: tag }));
}

/** Models for a route, sorted into provider groups (reliable first). */
export function modelOptions(models: AvailableModel[], routeId: string) {
  const vision = isVisionRoute(routeId);
  const groups = new Map<string, AvailableModel[]>();
  for (const m of [...models].sort((a, b) => Number(b.reliable) - Number(a.reliable) || a.name.localeCompare(b.name))) {
    if (m.vision_only && !vision) continue;
    const key = m.provider || "Other";
    groups.set(key, [...(groups.get(key) ?? []), m]);
  }
  return [...groups].sort(([a], [b]) => a.localeCompare(b)).map(([group, items]) => ({ group, items: items.map((m) => ({ value: m.tag, label: m.name })) }));
}
